import React, { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  Droplets,
  DollarSign,
  Calendar,
  Download,
  Activity,
  Minus
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

export const Analytics = () => {
  const { user } = useAuth();
  const [period, setPeriod] = useState('month');
  const [usageData, setUsageData] = useState(null);
  const [trendsData, setTrendsData] = useState(null);
  const [predictionsData, setPredictionsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('usage');
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState('');

  useEffect(() => {
    fetchDevices();
  }, [period]);
  
  useEffect(() => {
    if (!selectedDevice) return;

    fetchAnalytics();
  }, [selectedDevice]);

  const fetchDevices = async () => {
    try {
      const token = localStorage.getItem('token');

      if (user.role === 'customer')
      {
        const response = await axios.get(`${API}/customers/${user.id}/devices`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        setDevices(response.data.data);
      }
      else
      {
        const response = await axios.get(`${API}/devices`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        setDevices(response.data);
      }

      if (devices.length > 0) {
        setSelectedDevice(devices[0]);
        fetchAnalytics();
      }
    } catch (error) {
      setDevices([]);
    }
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch usage data
      const usageResponse = await axios.get(
        `${API}/water-usage/analytics?period=${period}&device_id=${selectedDevice}`,
        { headers }
      );
      setUsageData(usageResponse.data.data);

      // Fetch predictions
      if (user.role === 'customer' || user.id) {
        try {
          const predictionsResponse = await axios.get(
            `${BACKEND_URL}/api/analytics/predictions?days_ahead=7`,
            { headers }
          );
          setPredictionsData(predictionsResponse.data);
        } catch (error) {
          // Predictions might not be available for all users
          console.log('Predictions not available');
        }
      }

    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics', { id: 'analytics-load-error' });
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    try {
      const token = localStorage.getItem('token');
      
      const requestData = {
        start_date: usageData.start_date.split('T')[0],
        end_date: usageData.end_date.split('T')[0],
        include_charts: true
      };

      const endpoint = format === 'pdf' ? 'export-pdf' : 'export-excel';
      
      const response = await axios.post(
        `${BACKEND_URL}/api/reports/${endpoint}`,
        requestData,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `water_usage_report.${format === 'pdf' ? 'pdf' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success(`${format.toUpperCase()} report downloaded`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error(`Failed to export ${format.toUpperCase()}`);
    }
  };

  const getTrendIcon = (trend) => {
    if (trend === 'increasing') return <TrendingUp className="h-5 w-5 text-red-500" />;
    if (trend === 'decreasing') return <TrendingDown className="h-5 w-5 text-green-500" />;
    return <Minus className="h-5 w-5 text-gray-500" />;
  };

  const formatChartData = (dataPoints) => {
    return dataPoints.map(point => ({
      date: point.period.split('T')[0],
      consumption: parseFloat(point?.volume?.toFixed(3) || 0),
      cost: parseFloat(point?.cost?.toFixed(2) || 0)
    }));
  };

  if (loading && !usageData) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Activity className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading analytics...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Water Usage Analytics</h1>
          <p className="text-gray-600">Track and analyze your water consumption patterns</p>
        </div>

        {/* Period Selector */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <select
              value={selectedDevice}
              onChange={(e) => {
                const value = e.target.value;
                setSelectedDevice(value);
              }}
              className="border border-gray-300 rounded-lg px-4 py-2"
            >
              <option>Choose device</option>
              {devices.map(device => (
                <option key={device.id} value={device.id}>{device.device_name}</option>
              ))}
            </select>
          </div>
          <div className="flex gap-2">
            {['day', 'week', 'month', 'year'].map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-4 py-2 rounded-lg font-medium transition ${
                  period === p
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {usageData && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-xl shadow-md p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-600 text-sm">Total Consumption</span>
                  <Droplets className="h-5 w-5 text-blue-600" />
                </div>
                <p className="text-2xl font-bold text-gray-900">
                  {usageData?.summary?.total_volume?.toFixed(2) || 0} m³
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Avg: {usageData?.summary?.average_daily_volume?.toFixed(3) || 0} m³/day
                </p>
              </div>

              <div className="bg-white rounded-xl shadow-md p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-600 text-sm">Total Cost</span>
                  <DollarSign className="h-5 w-5 text-green-600" />
                </div>
                <p className="text-2xl font-bold text-gray-900">
                  Rp {usageData?.summary?.total_cost?.toLocaleString('id-ID')}
                </p>
              </div>

              <div className="bg-white rounded-xl shadow-md p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-600 text-sm">Trend</span>
                  <Calendar className="h-5 w-5 text-orange-600" />
                </div>
                <div className="flex items-center gap-2">
                  {trendsData && getTrendIcon(trendsData.overall_trend)}
                  <span className="text-2xl font-bold text-gray-900 capitalize">
                    {trendsData?.summary?.trend || 'Stable'}
                  </span>
                </div>
                {trendsData && (
                  <p className="text-sm text-gray-500 mt-1">
                    {trendsData.growth_rate > 0 ? '+' : ''}
                    {trendsData.growth_rate.toFixed(1)}% change
                  </p>
                )}
              </div>
            </div>

            {/* Charts */}
            <div className="bg-white rounded-xl shadow-md p-6 mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Daily Consumption</h2>
              <ResponsiveContainer width="100%" height={400}>
                <AreaChart data={formatChartData(usageData?.breakdown || [])}>
                  <defs>
                    <linearGradient id="colorConsumption" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => value.split('-').slice(1).join('/')}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip
                    formatter={(value, name) => {
                      if (name === 'consumption') return [value + ' m³', 'Consumption'];
                      if (name === 'cost') return ['Rp ' + value?.toLocaleString('id-ID'), 'Cost'];
                      return [value, name];
                    }}
                  />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="consumption"
                    stroke="#3B82F6"
                    fillOpacity={1}
                    fill="url(#colorConsumption)"
                    name="Consumption (m³)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};
